// External libraries
//@ts-expect-error untyped import
import { hasOwnProp } from '@checkout/react-components';
//@ts-expect-error untyped import
import { PAYPAL as ULInject } from 'ul-inline/dist/inject';

// Internal modules
import { logger } from '@/lib/logger';
import { getGlobals, getLocale } from '@/lib';
import { isFunction } from '@/lib/utils/typeUtils';

/**
 * @function dropOneTouchCookie
 * @param {string} token Checkout token
 * @returns
 * @description injects an iframe from unified login onto the page in order
 * to exchange id_token for refresh_token in case of one touch
 */

const dropOneTouchCookie = (
  token: string,
  timeoutValue = 10 * 1000,
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    let timeoutPromise: NodeJS.Timeout;

    const startTimer = () => {
      timeoutPromise = setTimeout(() => {
        reject('one_touch_timeout');
      }, timeoutValue);
    };

    const failureHandler = (err: Event | string) => {
      clearTimeout(timeoutPromise);
      const message = typeof err === 'object' ? err?.message : err;
      reject(message || 'one_touch_error');
    };

    const ulEventHandlers = {
      oneTouchInterstitialOptInFailed: (err: Event | string) =>
        failureHandler(err),
      oneTouchInterstitialOptInNotAuthorized: (err: Event | string) => {
        // Removing the ulTarget element from DOM to avoid any other accidental
        // actions on the element
        document.getElementById('ulTarget')?.remove();
        failureHandler(err);
      },
      oneTouchInterstitialOptInSuccess: () => {
        clearTimeout(timeoutPromise);
        logger.info('one_touch_success');
        resolve();
      },

      // Don't do anything for these events - maybe we should just remove it
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      oneTouchInterstitialOptInAttempted: () => {}, // eslint-disable-line sort-keys
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      oneTouchInterstitialOptInLoaded: () => {},
    };

    type Event = {
      event: keyof typeof ulEventHandlers;
      message?: string;
    };

    type ResponseType = {
      data: Event;
      error: Event;
    };

    const postMessageHandler = (response: ResponseType): void => {
      logger.info('one_touch_post_response', response);
      const { event } = response.data || response.error;

      if (event && hasOwnProp(ulEventHandlers, event)) {
        const handler = ulEventHandlers[event];
        isFunction(handler) && handler(event);
      }
    };

    // Loads an iframe from unified login that drops the POMA cookie
    const ulTargetElement = document.createElement('div');
    ulTargetElement.setAttribute('id', 'ulTarget');

    // Hiding the iframe
    ulTargetElement.style.height = '0px';
    ulTargetElement.style.width = '0px';
    ulTargetElement.style.margin = '0px';
    ulTargetElement.style.padding = '0px';

    document.body.appendChild(ulTargetElement);

    const { country, locale } = getLocale();

    const options = {
      iframePathname: '/signin/activate-one-touch',
      onPostMessage: postMessageHandler,
      origin: getGlobals().uri.base,
      queryParams: {
        correlationId: getGlobals().correlationId,
        'country.x': country,
        flowId: token,
        flowName: 'checkoutOnlyMember', // default is poma flow
        'locale.x': locale,
      },
      targetElement: ulTargetElement,
    };

    // Timer is used to throw an error if we don't get a response from UL
    startTimer();
    ULInject.unifiedLogin.inject(options);
  });
};

/**
 * @function pomaLogout
 * @returns {Promise}
 * @description logouts for poma flow and resolves the promise with
 * `true` if it was a success or `false` if it wasn't
 */
const pomaLogout = (): Promise<boolean> => {
  return fetch('/checkoutweb/poma-logout')
    .then(() => {
      logger.info('poma_logout_success');
      return true;
    })
    .catch(({ message }) => {
      logger.error('poma_logout_error', { message });
      return false;
    });
};

type PomaToolTipRenderPositionProps = {
  questionButtonRef: React.RefObject<HTMLButtonElement>;
  tooltipContentRef: React.RefObject<HTMLUListElement>;
};
const getPomaTooltipRenderPosition = ({
  questionButtonRef,
  tooltipContentRef,
}: PomaToolTipRenderPositionProps): {
  arrowRightPosition: number | undefined;
  tooltipMarginTop: number | undefined;
} => {
  const tooltipHeightOffset = 60;
  const tooltipArrowOffset = 5;

  const questionIconElement = questionButtonRef?.current;
  const questionTooltipContentElement = tooltipContentRef?.current;

  let tooltipMarginTop, arrowRightPosition;

  if (questionTooltipContentElement) {
    tooltipMarginTop = -(
      questionTooltipContentElement?.offsetHeight + tooltipHeightOffset
    );
  }

  if (questionTooltipContentElement && questionIconElement) {
    arrowRightPosition =
      questionTooltipContentElement?.offsetWidth -
      questionIconElement?.offsetLeft +
      tooltipArrowOffset;
  }

  return { arrowRightPosition, tooltipMarginTop };
};

export { dropOneTouchCookie, getPomaTooltipRenderPosition, pomaLogout };
